import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUser } from '../hooks/useUser';

interface Permission {
  contests: string[];
  features: string[];
  contest_permissions: Record<string, { [key: string]: string[] }>;
}

export const useCheckPermissions = (requiredPermissions: string[] = []) => {
  const { user: authUser, isAuthenticated } = useAuth();
  const { user, isLoading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const checkPermissions = async () => {
      if (isLoading) return;

      // Si l'utilisateur n'est pas authentifié, rediriger vers la connexion
      if (!isAuthenticated || !authUser) {
        navigate('/admin/login');
        return;
      }

      // Si on est sur une route admin
      if (window.location.pathname.startsWith('/admin')) {
        // Vérifier si l'utilisateur a le rôle admin
        const isAdmin = user?.role === 'admin';
        console.log('Current user:', user); // Pour le débogage
        console.log('Is admin:', isAdmin); // Pour le débogage

        if (!isAdmin) {
          navigate('/admin/login');
          return;
        }
      }

      // Si on est sur une route client
      if (window.location.pathname.startsWith('/client')) {
        // Vérifier si l'utilisateur a le rôle client
        if (!user?.role || user.role !== 'client') {
          navigate('/client/login');
          return;
        }
        
        // Vérifier si le client est actif
        if (user.is_active === false) {
          // Déconnecter l'utilisateur inactif
          const { logout } = useAuth.getState();
          await logout();
          navigate('/client/login');
          return;
        }
      }

      // Vérifier les permissions spécifiques
      if (requiredPermissions.length > 0) {
        // Si 'admin' est requis, vérifier uniquement le rôle admin
        if (requiredPermissions.includes('admin')) {
          if (user?.role !== 'admin') {
            navigate('/admin/login');
            return;
          }
        } else {
          // Pour les autres permissions, vérifier dans features
          const hasPermissions = requiredPermissions.every(permission =>
            user?.permissions?.features?.includes(permission)
          );

          if (!hasPermissions) {
            navigate(user?.role === 'admin' ? '/admin/dashboard' : '/client/dashboard');
            return;
          }
        }
      }
    };

    let timeoutId: NodeJS.Timeout;
    
    // Attendre un court instant avant de vérifier les permissions
    // Cela permet aux opérations de création/modification de se terminer
    timeoutId = setTimeout(() => {
      checkPermissions();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, authUser, user, isLoading, navigate, requiredPermissions]);

  return { user, isLoading };
};

export const checkContestPermission = (
  user: any,
  contestId: string,
  requiredPermission: string
): boolean => {
  if (!user || user.role !== 'client') return false;

  const permissions = user.permissions as Permission;
  
  // Vérifier si le concours est dans la liste des concours autorisés
  if (!permissions.contests.includes(contestId)) return false;

  // Vérifier les permissions spécifiques pour ce concours
  const contestPermissions = permissions.contest_permissions[contestId] || {};
  return Object.values(contestPermissions).flat().includes(requiredPermission);
};
